import { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWalletClient, usePublicClient } from 'wagmi';
import { 
  getProvider, 
  getProviderFromWagmi,
  checkAllowanceWagmi,
  approveTokenWagmi,
  bridgeTokenWagmi,
  estimateBridgeFee,
  getTokenBalanceWagmi,
  checkTransactionStatus,
  checkCrossChainMessageStatus,
  resolveShidoTokenAddress
} from '../utils/web3';
import { CHAIN_CONFIGS, LOCAL_STORAGE_KEY, FEE_API_BASE_URL, ZERO_ADDRESS } from '../utils/constants';
import { TokenType, BridgeTransaction, TransactionState } from '../types';

export const useBridge = (walletAddress: string) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const [fromChain, setFromChain] = useState<string>('');
  const [toChain, setToChain] = useState<string>('');
  const [selectedToken, setSelectedToken] = useState<TokenType>('SHIDO');
  const [amount, setAmount] = useState<string>('');
  const [recipient, setRecipient] = useState<string>('');
  const [allowance, setAllowance] = useState<string>('0');
  const [balance, setBalance] = useState<string>('0');
  const [resolvedTokenAddress, setResolvedTokenAddress] = useState<string>('');
  const [tokenAvailable, setTokenAvailable] = useState<boolean>(true);
  const [fee, setFee] = useState<string>('0');
  const [estimatedReceiveAmount, setEstimatedReceiveAmount] = useState<string>('0');
  const [bridgeFee, setBridgeFee] = useState<string>('0');
  const [feeBreakdown, setFeeBreakdown] = useState<{
    transportFee: string;
    serviceFee: string;
    serviceFeePercentage: number;
  } | null>(null);
  const [transactions, setTransactions] = useState<BridgeTransaction[]>([]);
  const [transactionState, setTransactionState] = useState<TransactionState>({
    isLoading: false,
    error: null,
    success: false,
    hash: null,
  });

  // Load transactions from local storage - only on initial mount
  useEffect(() => {
    if (!walletAddress) return;
    
    try {
      const storedTransactions = localStorage.getItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`);
      if (storedTransactions) {
        const parsedTransactions = JSON.parse(storedTransactions);
        setTransactions(parsedTransactions);
      }
    } catch (error) {
      console.error('Error loading transactions from local storage:', error);
    }
  }, [walletAddress]); // Only run when wallet address changes

  // We'll handle localStorage updates manually in our functions instead of using this effect
  // This prevents race conditions and ensures immediate updates

  // Check pending transaction statuses
  useEffect(() => {
    if (!transactions.length) return;
    
    const checkPendingTransactions = async () => {
      // Check both pending and processing transactions
      const pendingTransactions = transactions.filter(tx => 
        tx.status === 'pending' || tx.status === 'processing'
      );
      
      if (!pendingTransactions.length) return;
      
      const updatedTransactions = [...transactions];
      let hasUpdates = false;
      
      // Process each transaction independently
      for (const tx of pendingTransactions) {
        try {
          const chainId = CHAIN_CONFIGS[tx.fromChain]?.chainId;
          if (!chainId) {
            continue;
          }
          
          // Get the current index of this transaction in the updatedTransactions array
          const txIndex = updatedTransactions.findIndex(t => t.hash === tx.hash);
          if (txIndex === -1) continue; // Skip if transaction not found
          
          // Always check the cross-chain message status first
          const crossChainResult = await checkCrossChainMessageStatus(tx.hash);
          
          // If the cross-chain status is completed or failed, use that
          if (crossChainResult.status === 'completed' || crossChainResult.status === 'failed') {
            updatedTransactions[txIndex] = { 
              ...updatedTransactions[txIndex], 
              status: crossChainResult.status,
              toChainTxHash: crossChainResult.toChainTxHash
            };
            hasUpdates = true;
            continue;
          }
          
          // If the cross-chain API returns "processing", update immediately
          if (crossChainResult.status === 'processing' && updatedTransactions[txIndex].status !== 'processing') {
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'processing' };
            hasUpdates = true;
            continue;
          }
          
          // If cross-chain status is still pending, check the source chain
          const sourceStatus = await checkTransactionStatus(tx.hash, chainId);
          
          if (sourceStatus === 'failed') {
            // If the source transaction failed, mark the whole bridge transaction as failed
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'failed' };
            hasUpdates = true;
            continue;
          }
          
          if (sourceStatus === 'completed' && updatedTransactions[txIndex].status === 'pending') {
            // If the source transaction is completed but cross-chain is not, mark as processing
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'processing' };
            hasUpdates = true;
          }
        } catch (error) {
          console.error(`Error checking status for transaction ${tx.hash}:`, error);
        }
      }
      
      if (hasUpdates) {
        setTransactions(updatedTransactions);
        
        // Also update localStorage
        if (walletAddress) {
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`, JSON.stringify(updatedTransactions));
          } catch (error) {
            console.error('Error saving updated transactions to local storage:', error);
          }
        }
      }
    };
    
    // Check immediately
    checkPendingTransactions();
    
    // Then check more frequently (every 10 seconds)
    const interval = setInterval(checkPendingTransactions, 10000);
    
    return () => clearInterval(interval);
  }, [transactions, walletAddress]); // Add walletAddress as a dependency

  // Set recipient to wallet address by default
  useEffect(() => {
    if (walletAddress && !recipient) {
      setRecipient(walletAddress);
    }
  }, [walletAddress, recipient]);

  // Resolve token address (native on Shido, wrapped on Ethereum)
  useEffect(() => {
    const resolveToken = async () => {
      if (!fromChain) {
        setResolvedTokenAddress('');
        setTokenAvailable(true);
        return;
      }

      try {
        const tokenAddress = await resolveShidoTokenAddress(CHAIN_CONFIGS[fromChain]);
        setResolvedTokenAddress(tokenAddress);

        if (fromChain !== 'shido-mainnet' && tokenAddress === ZERO_ADDRESS) {
          setTokenAvailable(false);
        } else {
          setTokenAvailable(true);
        }
      } catch (error) {
        console.error('Error resolving SHIDO token address:', error);
        setResolvedTokenAddress('');
        setTokenAvailable(false);
      }
    };

    resolveToken();
  }, [fromChain]);

  // Check allowance when necessary values change
  useEffect(() => {
    const checkTokenAllowance = async () => {
      if (!fromChain || !selectedToken || !walletAddress) {
        setAllowance('0');
        return;
      }

      try {
        const tokenAddress = resolvedTokenAddress;
        if (!tokenAddress || !tokenAvailable) {
          setAllowance('0');
          return;
        }
        if (tokenAddress === ZERO_ADDRESS) {
          setAllowance('0');
          return;
        }

        const bridgeAddress = CHAIN_CONFIGS[fromChain].bridge;
        
        // Use Wagmi function instead of provider-based function
        const allowanceAmount = await checkAllowanceWagmi(
          tokenAddress,
          walletAddress,
          bridgeAddress
        );

        setAllowance(allowanceAmount);
      } catch (error) {
        console.error('Error checking allowance:', error);
        setAllowance('0');
      }
    };

    checkTokenAllowance();
  }, [fromChain, selectedToken, walletAddress, resolvedTokenAddress, tokenAvailable]);

  // Get token balance when chain or token changes
  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (!fromChain || !selectedToken || !walletAddress) {
        setBalance('0');
        return;
      }

      try {
        const tokenAddress = resolvedTokenAddress;
        if (!tokenAddress || !tokenAvailable) {
          setBalance('0');
          return;
        }

        if (tokenAddress === ZERO_ADDRESS) {
          let provider = null;
          if (walletClient) {
            provider = getProviderFromWagmi(walletClient);
          }
          if (!provider) {
            provider = await getProvider(CHAIN_CONFIGS[fromChain].chainId);
          }

          if (!provider) {
            setBalance('0');
            return;
          }

          const balanceWei = await provider.getBalance(walletAddress);
          setBalance(ethers.utils.formatEther(balanceWei));
          return;
        }

        // Use Wagmi function instead of provider-based function
        const balanceAmount = await getTokenBalanceWagmi(
          tokenAddress,
          walletAddress
        );

        setBalance(balanceAmount);
      } catch (error) {
        console.error('Error getting token balance:', error);
        setBalance('0');
      }
    };

    fetchTokenBalance();
  }, [fromChain, selectedToken, walletAddress, resolvedTokenAddress, tokenAvailable, walletClient]);

  // Estimate fee when from/to chains change
  useEffect(() => {
    const estimateFee = async () => {
      if (!fromChain || !toChain) {
        setFee('0');
        setBridgeFee('0');
        return;
      }
      if (!tokenAvailable) {
        setFee('0');
        setBridgeFee('0');
        setFeeBreakdown(null);
        return;
      }

      try {
        // Try to use Wagmi wallet client first, fallback to legacy provider
        let provider = null;
        if (walletClient) {
          provider = getProviderFromWagmi(walletClient);
        }
        
        if (!provider) {
          provider = await getProvider(CHAIN_CONFIGS[fromChain].chainId);
        }
        
        if (!provider) {
          console.warn('No provider available for fee estimation, skipping network fee');
          setFee('0');
        } else {
          const feeAmount = await estimateBridgeFee(
            CHAIN_CONFIGS[fromChain],
            CHAIN_CONFIGS[toChain],
            provider
          );
          setFee(feeAmount);
        }

        // Get bridge fee from Fee API
        if (recipient && amount && parseFloat(amount) > 0) {
          try {
            // Ensure amount is limited to 6 decimal places before sending to API
            let truncatedAmount = amount;
            if (truncatedAmount && truncatedAmount.includes('.')) {
              const parts = truncatedAmount.split('.');
              if (parts[1] && parts[1].length > 6) {
                truncatedAmount = parts[0] + '.' + parts[1].substring(0, 6);
              }
            }
            
            const requestBody = {
              toChainId: CHAIN_CONFIGS[toChain].bip44ChainId,
              gasLimit: 1500000, // Use 1.5M gas for first mint as per README
              userAddress: recipient,
              feeToken: resolvedTokenAddress,
              bridgeAmount: truncatedAmount, // Include bridge amount for service fee calculation
            };

            const feeResponse = await fetch(`${FEE_API_BASE_URL}/api/fee/estimate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            });

            if (feeResponse.ok) {
              const feeResult = await feeResponse.json();
              if (feeResult.success) {
                setBridgeFee(feeResult.data.fee);
                setFeeBreakdown(feeResult.data.feeBreakdown || null);
              }
            }
          } catch (error) {
            console.error('Error getting bridge fee estimate:', error);
            setBridgeFee('0');
            setFeeBreakdown(null);
          }
        } else {
          setBridgeFee('0');
          setFeeBreakdown(null);
        }
      } catch (error) {
        console.error('Error estimating fee:', error);
        setFee('0');
        setBridgeFee('0');
      }
    };

    estimateFee();
  }, [fromChain, toChain, recipient, selectedToken, amount, resolvedTokenAddress, tokenAvailable]);

  // Calculate estimated receive amount when amount or bridge fee changes
  useEffect(() => {
    if (!amount || !bridgeFee) {
      setEstimatedReceiveAmount('0');
      return;
    }

    try {
      const amountNum = parseFloat(amount);
      const feeNum = parseFloat(bridgeFee);
      
      if (amountNum > feeNum) {
        const receiveAmount = amountNum - feeNum;
        setEstimatedReceiveAmount(receiveAmount.toFixed(6));
      } else {
        setEstimatedReceiveAmount('0');
      }
    } catch (error) {
      console.error('Error calculating estimated receive amount:', error);
      setEstimatedReceiveAmount('0');
    }
  }, [amount, bridgeFee]);

  // Handle from chain change
  const handleFromChainChange = useCallback((chainId: string) => {
    setFromChain(chainId);
    
    // Auto-pick the opposite chain since we only support Shido <-> Ethereum
    if (chainId === 'shido-mainnet') {
      setToChain('ethereum');
    } else {
      setToChain('shido-mainnet');
    }
  }, []);

  // Handle to chain change
  const handleToChainChange = (chainId: string) => {
    setToChain(chainId);
  };

  // Handle token change
  const handleTokenChange = (token: TokenType) => {
    setSelectedToken(token);
  };

  // Handle amount change
  const handleAmountChange = (value: string) => {
    // Limit decimal places to prevent API parsing errors
    // Limit to 6 decimal places maximum (e.g., 23.8777266 becomes 23.877726)
    if (value && value.includes('.')) {
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 6) {
        // Truncate to 6 decimal places
        value = parts[0] + '.' + parts[1].substring(0, 6);
      }
    }
    setAmount(value);
  };

  // Handle recipient change
  const handleRecipientChange = (address: string) => {
    setRecipient(address);
  };

  // Approve token
  const handleApproveToken = async () => {
    if (!fromChain || !selectedToken || !amount || !walletAddress) {
      setTransactionState({
        isLoading: false,
        error: 'Missing required fields',
        success: false,
        hash: null,
      });
      return;
    }

    setTransactionState({
      isLoading: true,
      error: null,
      success: false,
      hash: null,
    });

    try {
      const tokenAddress = resolvedTokenAddress;
      if (!tokenAddress || !tokenAvailable) {
        throw new Error(`${selectedToken} not available on ${CHAIN_CONFIGS[fromChain].name}`);
      }
      if (tokenAddress === ZERO_ADDRESS) {
        throw new Error('Native SHIDO does not require approval');
      }

      const bridgeAddress = CHAIN_CONFIGS[fromChain].bridge;
      
      // Use Wagmi function instead of provider-based function
      const txHash = await approveTokenWagmi(
        tokenAddress,
        bridgeAddress,
        amount
      );

      // Determine if this is a slow chain (Ethereum) or fast chain (Shido)
      const isSlowChain = fromChain === 'ethereum';

      if (isSlowChain && publicClient) {
        // For slow chains like Ethereum, wait for transaction confirmation
        // to avoid "approval not confirmed" errors when bridging
        try {
          await publicClient.waitForTransactionReceipt({ 
            hash: txHash as `0x${string}`,
            confirmations: 1
          });
          
          // After confirmation, verify the allowance
          const verifiedAllowance = await checkAllowanceWagmi(
            tokenAddress,
            walletAddress,
            bridgeAddress
          );
          setAllowance(verifiedAllowance);
        } catch (confirmError) {
          console.warn('Transaction confirmation failed, using optimistic update:', confirmError);
          // Fallback to optimistic update if confirmation fails
          setAllowance(amount);
        }
      } else {
        // For fast chains like Shido, use optimistic update
        // This fixes the race condition where RPC returns stale data
        setAllowance(amount);

        // Also verify in background with a delay (but don't block on it)
        setTimeout(async () => {
          try {
            const verifiedAllowance = await checkAllowanceWagmi(
              tokenAddress,
              walletAddress,
              bridgeAddress
            );
            // Only update if verified allowance is sufficient
            const verifiedNum = parseFloat(verifiedAllowance);
            const amountNum = parseFloat(amount);
            if (verifiedNum >= amountNum) {
              setAllowance(verifiedAllowance);
            }
          } catch (e) {
            console.warn('Background allowance verification failed:', e);
          }
        }, 2000);
      }

      setTransactionState({
        isLoading: false,
        error: null,
        success: true,
        hash: txHash,
      });
    } catch (error: any) {
      console.error('Error approving token:', error);
      setTransactionState({
        isLoading: false,
        error: error.message || 'Failed to approve token',
        success: false,
        hash: null,
      });
    }
  };

  // Bridge token - This function adds a new transaction to the history
  const handleBridgeToken = async () => {
    if (!fromChain || !toChain || !selectedToken || !amount || !recipient) {
      setTransactionState({
        isLoading: false,
        error: 'Missing required fields',
        success: false,
        hash: null,
      });
      return;
    }
    if (!tokenAvailable) {
      setTransactionState({
        isLoading: false,
        error: 'Wrapped SHIDO not found on Ethereum. Bridge from Shido first.',
        success: false,
        hash: null,
      });
      return;
    }

    setTransactionState({
      isLoading: true,
      error: null,
      success: false,
      hash: null,
    });

    try {
      // Get fee data from the Fee API
      let feeData;
      try {
        // Ensure amount is limited to 6 decimal places before sending to API
        let truncatedAmount = amount;
        if (truncatedAmount && truncatedAmount.includes('.')) {
          const parts = truncatedAmount.split('.');
          if (parts[1] && parts[1].length > 6) {
            truncatedAmount = parts[0] + '.' + parts[1].substring(0, 6);
          }
        }
        
        const requestBody = {
          toChainId: CHAIN_CONFIGS[toChain].bip44ChainId,
          gasLimit: 1500000, // Use 1.5M gas for first mint as per README
          userAddress: recipient,
          feeToken: resolvedTokenAddress, // Use the token address as fee token
          bridgeAmount: truncatedAmount, // Include bridge amount for service fee calculation
        };

        console.log('Fee API request:', requestBody);

        const feeResponse = await fetch(`${FEE_API_BASE_URL}/api/fee/estimate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        console.log('Fee API response status:', feeResponse.status);

        if (!feeResponse.ok) {
          const errorText = await feeResponse.text();
          console.error('Fee API error response:', errorText);
          throw new Error(`Fee API returned ${feeResponse.status}: ${errorText}`);
        }

        const feeResult = await feeResponse.json();
        console.log('Fee API result:', feeResult);

        if (!feeResult.success) {
          throw new Error(feeResult.error || 'Failed to get fee estimate');
        }

        feeData = {
          fee: feeResult.data.fee,
          deadline: feeResult.data.deadline,
          signature: feeResult.data.signature,
        };

        console.log('Fee data prepared:', feeData);
      } catch (feeError) {
        console.error('Error getting fee estimate:', feeError);
        throw new Error(`Failed to get fee estimate: ${feeError.message}`);
      }

      // Use Wagmi function instead of provider-based function
      const txHash = await bridgeTokenWagmi(
        CHAIN_CONFIGS[fromChain],
        CHAIN_CONFIGS[toChain],
        selectedToken,
        amount,
        recipient,
        feeData
      );

      // Add transaction to history
      const newTransaction: BridgeTransaction = {
        hash: txHash,
        fromChain,
        toChain,
        token: selectedToken,
        amount,
        status: 'pending',
        timestamp: Date.now(),
        recipient,
      };
      
      // Update transactions state immediately
      const updatedTransactions = [newTransaction, ...transactions];
      
      // Force a re-render by using setTimeout
      setTimeout(() => {
        setTransactions(updatedTransactions);
        
        // Also update localStorage immediately
        if (walletAddress) {
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`, JSON.stringify(updatedTransactions));
          } catch (error) {
            console.error('Error saving transactions to local storage:', error);
          }
        }
      }, 0);

      setTransactionState({
        isLoading: false,
        error: null,
        success: true,
        hash: txHash,
      });

      // Reset form
      setAmount('');
    } catch (error: any) {
      console.error('Error bridging token:', error);
      setTransactionState({
        isLoading: false,
        error: error.message || 'Failed to bridge token',
        success: false,
        hash: null,
      });
    }
  };

  // Check if approval is needed
  const needsApproval = () => {
    if (!amount || !allowance || !fromChain) return false;
    if (!tokenAvailable) return false;
    if (resolvedTokenAddress === ZERO_ADDRESS) return false;
    
    try {
      const decimals = 18;
      
      const amountBN = ethers.utils.parseUnits(amount, decimals);
      const allowanceBN = ethers.utils.parseUnits(allowance, decimals);
      
      return amountBN.gt(allowanceBN);
    } catch (error) {
      console.error('Error checking if approval is needed:', error);
      return true;
    }
  };

  // Clear transaction history
  const clearTransactions = useCallback(() => {
    // Clear state
    setTransactions([]);
    
    // Clear localStorage
    if (walletAddress) {
      try {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`);
      } catch (error) {
        console.error('Error removing transactions from local storage:', error);
      }
    }
  }, [walletAddress]);

  return {
    fromChain,
    toChain,
    selectedToken,
    amount,
    recipient,
    allowance,
    balance,
    fee,
    estimatedReceiveAmount,
    bridgeFee,
    feeBreakdown,
    transactions,
    transactionState,
    tokenAvailable,
    handleFromChainChange,
    handleToChainChange,
    handleTokenChange,
    handleAmountChange,
    handleRecipientChange,
    handleApproveToken,
    handleBridgeToken,
    needsApproval,
    clearTransactions,
  };
};
